import React, { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Flame } from 'lucide-react';
import AppShell from '../components/AppShell';
import axiosInstance from '../utils/axiosInstance';

const STATUS = {
  implemented: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Implemented' },
  stub:        { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Stub (fake data)' },
  missing:     { icon: XCircle,      color: 'text-rose-600',   bg: 'bg-rose-50', label: 'Not built yet' },
};

const PRIORITY = {
  high:   { color: 'text-rose-700',    bg: 'bg-rose-50',    label: 'HIGH' },
  medium: { color: 'text-amber-700',   bg: 'bg-amber-50',   label: 'MEDIUM' },
  low:    { color: 'text-stone-600',   bg: 'bg-stone-100',  label: 'LOW' },
};

const Endpoints = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axiosInstance.get('api/replai/endpoints/').then(({ data }) => setItems(data));
  }, []);

  // Group by category and keep the order driven by backend (already
  // sorted by ticket_volume desc). First-seen category wins the slot.
  const grouped = items.reduce((acc, it) => {
    if (!acc[it.category]) acc[it.category] = [];
    acc[it.category].push(it);
    return acc;
  }, {});
  const orderedCategories = Object.keys(grouped);

  return (
    <AppShell
      title="Endpoints"
      subtitle="All the data Replai needs to answer tickets. Sorted by ticket volume - busiest categories first."
    >
      <div className="space-y-8">
        {orderedCategories.map((category) => {
          const rows = grouped[category];
          const volume = rows[0]?.ticket_volume ?? 0;
          const priority = rows[0]?.priority ?? 'low';
          const p = PRIORITY[priority];
          return (
          <section key={category}>
            <div className="flex items-center gap-3 mb-3">
              <h3 className="font-display text-xl font-semibold">{category}</h3>
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${p.bg} ${p.color}`}>
                {priority === 'high' && <Flame className="h-3 w-3" />}
                {p.label}
              </span>
              <span className="text-xs text-muted-foreground">
                {volume} ticket{volume === 1 ? '' : 's'} in current view
              </span>
            </div>
            <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-[hsl(var(--secondary))]">
                  <tr>
                    <th className="px-4 py-3">Endpoint</th>
                    <th className="px-4 py-3">Purpose</th>
                    <th className="px-4 py-3">Data source</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/70">
                  {rows.map((r) => {
                    const s = STATUS[r.status];
                    const Icon = s.icon;
                    return (
                      <tr key={r.name} className="hover:bg-[hsl(var(--secondary)/0.4)]">
                        <td className="px-4 py-3 font-mono text-xs">{r.name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{r.purpose}</td>
                        <td className="px-4 py-3 text-muted-foreground">{r.source}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${s.bg} ${s.color}`}>
                            <Icon className="h-3 w-3" /> {s.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
          );
        })}
      </div>
    </AppShell>
  );
};

export default Endpoints;
